import { api } from '@/lib/api';
import { Department, Doctor, OpdFee, Staff, StaffFormData, StaffOnboardingFiles, TempShift, HospitalStaff } from '@/types/types';
import { Shift } from '@/types/types';
import { HospitalAdminKpis } from '@/types/kpis';

function appendStaffFormFields(fd: FormData, data: Partial<StaffFormData>) {
    if (data.name != null) fd.append('name', data.name);
    if (data.email != null) fd.append('email', data.email);
    if (data.password) fd.append('password', data.password);
    if (data.role != null) fd.append('role', data.role);
    if (data.specialisation != null) fd.append('specialisation', data.specialisation);
    if (data.qualification != null) fd.append('qualification', data.qualification);
    if (data.description != null) fd.append('description', data.description);
    if (data.deptId != null) fd.append('deptId', data.deptId);
    if (data.status != null) fd.append('status', data.status);
    if (typeof data.isSalaried === 'boolean') fd.append('isSalaried', String(data.isSalaried));
    if (data.panNumber != null) fd.append('panNumber', data.panNumber);
    if (data.registrationNumber != null) fd.append('registrationNumber', data.registrationNumber);
    if (typeof data.isLeadAgency === 'boolean') fd.append('isLeadAgency', String(data.isLeadAgency));
    if (data.leadAgencyExtraSources?.length) {
        fd.append('leadAgencyExtraSources', JSON.stringify(data.leadAgencyExtraSources));
    }
    if (data.investorReportKeys?.length) {
        fd.append('investorReportKeys', JSON.stringify(data.investorReportKeys));
    }
    if (data.hospitalVendorId) fd.append('hospitalVendorId', data.hospitalVendorId);
    if (typeof data.requiresOpsApproval === 'boolean') {
        fd.append('requiresOpsApproval', String(data.requiresOpsApproval));
    }
    if (data.vendorCategoryId) fd.append('vendorCategoryId', data.vendorCategoryId);
    if (data.staffHospitalScope) fd.append('staffHospitalScope', data.staffHospitalScope);
    if (data.hospitalId) fd.append('hospitalId', data.hospitalId);
    if (data.staffHospitalIds !== undefined) {
        fd.append('staffHospitalIds', JSON.stringify(data.staffHospitalIds));
    }
    if (data.doctorTeamLeadId != null) fd.append('doctorTeamLeadId', data.doctorTeamLeadId);
    if (typeof data.pnlAestheticTeamLead === 'boolean') {
        fd.append('pnlAestheticTeamLead', String(data.pnlAestheticTeamLead));
    }
    if (data.teamPortalPrimaryColor != null) fd.append('teamPortalPrimaryColor', data.teamPortalPrimaryColor);
    if (data.teamPortalSecondaryColor != null) fd.append('teamPortalSecondaryColor', data.teamPortalSecondaryColor);
    if (data.teamPortalAccentColor != null) fd.append('teamPortalAccentColor', data.teamPortalAccentColor);
    if (data.teamPortalSidebarBgColor != null) fd.append('teamPortalSidebarBgColor', data.teamPortalSidebarBgColor);
    if (typeof data.teamPortalCustomUiColors === 'boolean') {
        fd.append('teamPortalCustomUiColors', String(data.teamPortalCustomUiColors));
    }
}

function appendStaffOnboardingFiles(fd: FormData, files?: StaffOnboardingFiles) {
    if (!files) return;
    if (files.panCard) fd.append('panCard', files.panCard);
    if (files.cancelledCheque) fd.append('cancelledCheque', files.cancelledCheque);
    if (files.registrationDocument) fd.append('registrationDocument', files.registrationDocument);
    if (files.indemnityInsurance) fd.append('indemnityInsurance', files.indemnityInsurance);
    if (files.teamPortalLogo) fd.append('teamPortalLogo', files.teamPortalLogo);
}

export interface SarvamAgentConfigRow {
    id: string;
    sarvamAppId: string;
    sarvamAppVersion: string | null;
    sarvamConnectionId: string | null;
    agentPhoneNumber: string | null;
    name: string;
    purpose: string | null;
    enabled: boolean;
}

export interface SarvamLiveToolEndpoints {
    listDoctors: string;
    listSlots: string;
    bookAppointment: string;
}

export interface SarvamIntegrationCatalogItem {
    id: string;
    name: string;
    description: string;
    status: 'active' | 'planned' | 'disabled';
}

export interface SarvamVoiceAiSettings {
    enabled: boolean;
    agentEnabled: boolean;
    sarvamOrgId: string;
    sarvamWorkspaceId: string;
    hasApiKey: boolean;
    defaultAgentId: string | null;
    environment: 'STAGING' | 'PRODUCTION';
    callingWindow: {
        startMinutes?: number;
        endMinutesExclusive?: number;
        startTime?: string;
        endTime?: string;
        /** @deprecated legacy hour fields */
        startHour?: number;
        endHour?: number;
    };
    maxSubmitAttempts: number;
    leadSourcesAllowlist: string[];
    campaignNamesAllowlist: string[];
    assignedToStaffIdsAllowlist: string[];
    /** Empty = all eligible in-house sales (round-robin after Sarvam OPD book) */
    sarvamOpdAssignStaffIdsAllowlist?: string[];
    callUnassignedLeads: boolean;
    callDelayMinutes: number;
    rolloutMode: 'TEST' | 'LIVE';
    inputVariableContract: string[];
    inputVariableHints?: string[] | null;
    submitInputKeys?: string[] | null;
    outputVariableHints: string[];
    liveToolEndpoints?: SarvamLiveToolEndpoints | null;
    toolsAuthHeader?: string;
    toolsBaseUrl?: string;
    agents: SarvamAgentConfigRow[];
    webhookUrl?: string;
    integrations?: SarvamIntegrationCatalogItem[];
    appointmentReminderEnabled?: boolean;
    appointmentReminderHoursBefore?: number;
    appointmentReminderHoursBeforeNote?: string;
    appointmentReminderAgentAppId?: string | null;
    appointmentReminderRolloutMode?: 'TEST' | 'LIVE';
    appointmentReminderLeadSourcesAllowlist?: string[];
    appointmentReminderInputVariableContract?: string[];
    appointmentReminderOutputVariableContract?: string[];
    dnpNotReachableRetryEnabled?: boolean;
    dnpNotReachableAgentAppId?: string | null;
    dnpNotReachableRolloutMode?: 'TEST' | 'LIVE';
    dnpPastLeadsAgeBuckets?: SarvamDnpPastAgeBuckets;
    dnpNotReachableScheduleNote?: string;
}

export type SarvamDnpPastAgeBuckets = {
    days_0_30: boolean;
    days_30_60: boolean;
    days_60_90: boolean;
    days_90_plus: boolean;
};

export interface SarvamBillingSummary {
    from: string | null;
    to: string | null;
    ratePerMinuteInr: number;
    totalCostInr: number;
    callCount: number;
    completedCallCount: number;
    totalDurationSeconds: number;
    avgCostPerCallInr: number | null;
    byHospital: Array<{
        hospitalId: string;
        hospitalName: string;
        callCount: number;
        totalCostInr: number;
        totalDurationSeconds: number;
    }>;
    byDay: Array<{ date: string; callCount: number; totalCostInr: number }>;
    note: string;
}

export interface SarvamPricingRatesResponse {
    effectiveVoiceRatePerMinuteInr: number;
    rates: Array<{
        id: string;
        rateKey: string;
        amountInr: number;
        effectiveFrom: string | null;
        effectiveTo: string | null;
    }>;
}

export interface SarvamLeadCallRow {
    id: string;
    leadId: string;
    hospitalId: string;
    sarvamAppId: string | null;
    sarvamCampaignId?: string | null;
    sarvamAttemptId: string | null;
    processingStatus: string;
    sarvamCallStatus: string | null;
    durationSeconds: number | null;
    estimatedCostInr: number | null;
    submittedAt: string | null;
    completedAt: string | null;
    lastErrorMessage: string | null;
    createdAt: string;
    lead: { patientName: string | null; phone: string | null };
    hospital: { name: string };
    agentLabel?: string;
    agentPurpose?: string | null;
    campaignLabel?: string;
    callSummary?: string | null;
    crmEffectSummary?: string | null;
    appointmentReminderDetail?: string | null;
    leadSource?: string | null;
    leadSourceLabel?: string | null;
    crmOpdBooked?: boolean;
    reminderConfirmed?: boolean;
    dnpActivated?: boolean;
    dnpOpdBooked?: boolean;
    assignedToId?: string | null;
    assignedToName?: string | null;
}

export interface SarvamLeadCallLogDaySummaryRow {
    date: string;
    crmOpdBooked: number;
    reminderConfirmed: number;
    crmCallsTotal: number;
    reminderCallsTotal: number;
    dnpCallsTotal: number;
    dnpActivated: number;
    dnpOpdBooked: number;
}

export interface SarvamCallLogLeadRef {
    leadId: string;
    patientName: string | null;
    phone: string | null;
}

export interface SarvamLeadCallLogListResponse {
    calls: SarvamLeadCallRow[];
    summary: {
        totals: {
            crmOpdBooked: number;
            reminderConfirmed: number;
            crmCallsTotal: number;
            reminderCallsTotal: number;
            dnpCallsTotal: number;
            dnpActivated: number;
            dnpActivatedAllTime: number;
            dnpOpdBooked: number;
            dnpOpdBookedAllTime: number;
            dnpLostAllTime: number;
            opdVisitedReminderNotConfirmed: number;
            opdVisitedReminderConfirmed: number;
        };
        byDay: SarvamLeadCallLogDaySummaryRow[];
        drillDown?: {
            crmOpdBooked: SarvamCallLogLeadRef[];
            reminderConfirmed: SarvamCallLogLeadRef[];
            dnpActivated: SarvamCallLogLeadRef[];
            dnpOpdBooked: SarvamCallLogLeadRef[];
            dnpActivatedAllTime: SarvamCallLogLeadRef[];
            dnpOpdBookedAllTime: SarvamCallLogLeadRef[];
            dnpLostAllTime: SarvamCallLogLeadRef[];
            opdVisitedReminderNotConfirmed: SarvamCallLogLeadRef[];
            opdVisitedReminderConfirmed: SarvamCallLogLeadRef[];
        };
    };
}

export interface SarvamDispositionMappingRow {
    id?: string;
    outputVariableKey: string;
    outputVariableValue: string;
    targetStatus: string | null;
    targetSubStatus: string | null;
    scheduleFollowUp?: boolean;
    followUpOffsetMinutes?: number | null;
    priority?: number;
    enabled?: boolean;
    notes?: string | null;
}

export interface SarvamDoctorMappingRow {
    id?: string;
    sarvamAlias: string;
    displayAlias?: string | null;
    hospitalStaffId: string;
    staffName?: string;
    hospitalName?: string;
    priority?: number;
    enabled?: boolean;
    notes?: string | null;
}

export interface SarvamCampaignProcedureMappingRow {
    id?: string;
    campaignPattern: string;
    procedureName: string;
    priority?: number;
    enabled?: boolean;
    notes?: string | null;
}

export interface RoundRobinConfig {
    leadRoundRobinEnabled: boolean;
    leadRoundRobinCursor: number;
}

export interface AgencyUserOption {
    id: string;
    name: string;
    email: string;
    isLeadAgency: boolean;
}

export interface RoundRobinAssignee {
    id: string;
    name: string;
    email: string;
    status: 'ACTIVE' | 'INACTIVE';
    isLeadRoundRobinActive: boolean;
    exitedAt?: string | null;
    reportsToManagerId?: string | null;
    /** Empty = all lead sources allowed for auto-assignment */
    leadDeniedSources?: string[];
    reportsToManager?: {
        id: string;
        name: string;
        email: string;
    } | null;
}

export interface SalesManagerOption {
    id: string;
    name: string;
    email: string;
}

export interface RoundRobinAssigneePayload {
    assignees: RoundRobinAssignee[];
    managers: SalesManagerOption[];
}

export interface AgencyApiKeyRow {
    id: string;
    name: string;
    keyPrefix: string;
    isActive: boolean;
    lastUsedAt?: string | null;
    createdAt?: string;
    assignedToId: string;
    assignedTo?: {
        id: string;
        name: string;
        email: string;
        status?: string;
    };
    apiKey?: string;
}

export interface WhatsAppAutoReplyRule {
    id: string;
    name: string;
    keywords: string[];
    replyText: string;
    isActive: boolean;
    priority: number;
    createdAt?: string;
    updatedAt?: string;
}

export type MetaThmsAction =
    | 'CRM_LEAD_CREATED'
    | 'PATIENT_SELF_REGISTRATION'
    | 'CALLBACK_REQUEST'
    | 'APPOINTMENT_BOOKED'
    | 'OPD_CONSULTATION_COMPLETED'
    | 'DAY_CARE_REGISTRATION_COMPLETED'
    | 'IPD_ADMISSION_CREATED'
    | 'SURGERY_SCHEDULED'
    | 'SURGERY_COMPLETED'
    | 'IPD_FINAL_BILL_FROZEN'
    | 'IPD_PAYMENT_RECEIVED'
    | 'LOYALTY_APP_REGISTRATION';

export type MetaRevenueValueMode = 'CONSULTATION_FEE' | 'ADVANCE_RECEIVED' | 'ACTUAL_COLLECTED';

export type MetaEventQueueStatus = 'PENDING' | 'SENT' | 'FAILED' | 'DEAD';

/** @deprecated use MetaThmsAction */
export type MetaFunnelEvent = MetaThmsAction;

export interface MetaIntegrationConfig {
    enabled: boolean;
    datasetId: string;
    hasAccessToken: boolean;
    testMode: boolean;
    testEventCode: string;
    sendRevenueEvents: boolean;
    revenueValueMode: MetaRevenueValueMode;
    eventMapping: Record<MetaThmsAction, string>;
    defaultEventMapping: Record<MetaThmsAction, string>;
    /** custom_data.lead_event_source sent on Conversion Leads events. */
    crmLeadEventSource?: string;
    /** Suggested Events Manager funnel stage order. */
    crmFunnelStageOrder?: string[];
    usingEnvFallback: boolean;
}

export type GoogleEventQueueStatus = "PENDING" | "SENT" | "FAILED" | "DEAD";

export interface GoogleAdsIntegrationConfig {
  id: string;
  hospitalId: string;
  customerId?: string | null;
  loginCustomerId?: string | null;
  googleCloudProjectId?: string | null;
  clientId?: string | null;
  conversionLead?: string | null;
  conversionAppointmentBooked?: string | null;
  conversionOpdVisited?: string | null;
  conversionSurgeryBooked?: string | null;
  conversionPurchase?: string | null;
  isEnabled: boolean;
  hasDeveloperToken?: boolean;
  hasGoogleCloudProjectId?: boolean;
  hasClientSecret?: boolean;
  hasRefreshToken?: boolean;
  hasClientId?: boolean;
  hasCredentials?: boolean;
  usingEnvFallback?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GoogleEventQueueRow {
  id: string;
  hospitalId: string;
  leadId?: string | null;
  eventName: string;
  conversionValue?: number | null;
  status: GoogleEventQueueStatus;
  attempts: number;
  lastError?: string | null;
  nextAttemptAt?: string | null;
  createdAt: string;
  updatedAt: string;
  lead?: { id: string; patientName: string; phone: string } | null;
  payload?: Record<string, unknown> | null;
  logs?: Array<{
    id: string;
    ok: boolean;
    statusCode?: number | null;
    request?: any;
    response?: any;
    error?: string | null;
    createdAt: string;
  }>;
}

export interface MetaDashboardStats {
    leadsSentToday: number;
    registrationsSentToday: number;
    appointmentsSentToday: number;
    revenueEventsSentToday: number;
    successRate: number;
    failedEvents: number;
    datasetQualityScore: number | null;
    eventMatchQuality: number | null;
    revenueSentToMeta: number;
}

export interface MetaEventLogRow {
    id: string;
    hospitalId: string;
    leadId: string | null;
    patientId: string | null;
    thmsAction: MetaThmsAction;
    metaEventName: string;
    eventId: string;
    status: MetaEventQueueStatus;
    revenueAmount: number | null;
    lastErrorMessage: string | null;
    attemptCount: number;
    sentAt: string | null;
    createdAt: string;
    lead?: {
        id: string;
        patientName: string;
        phone: string;
    } | null;
    patient?: {
        id: string;
        name: string;
        phone: string;
    } | null;
    latestLog?: {
        requestPayload: unknown;
        responsePayload: unknown;
        errorMessage: string | null;
        attemptNumber: number;
    } | null;
}

/** @deprecated use MetaEventLogRow */
export type MetaConversionLogRow = MetaEventLogRow;

export interface CampaignAttributionRow {
    campaignId: string;
    campaignName: string | null;
    leads: number;
    registrations: number;
    appointments: number;
    admissions: number;
    surgeries: number;
    revenue: number;
    conversionRates: {
        leadToAppointment: number;
        appointmentToSurgery: number;
        revenuePerCampaign: number;
    };
}

export interface HospitalWhatsAppNumber {
    id: string;
    hospitalId: string;
    phoneNumberId: string;
    wabaId: string | null;
    name: string | null;
    leadIngestApiKeyId: string | null;
    leadIngestApiKey?: { id: string; name: string; keyPrefix: string; isActive: boolean } | null;
    isActive: boolean;
    isDefault: boolean;
    createdById: string | null;
    createdAt: string;
    updatedAt: string;
    accessTokenMasked: string;
}

interface ApiResponse<T> {
    data: T;
    message?: string;
    status?: string;
}
export const hospitalAdminApi = {
    // Departments
    getDepartments: (opts?: { hospitalId?: string }) =>
        api.get<ApiResponse<Department[]>>('/api/hospital-admin/departments', {
            params: opts?.hospitalId ? { hospitalId: opts.hospitalId } : undefined,
        }).then(res => res.data.data),
    createDepartment: (data: { name: string; description?: string; hospitalId?: string }) =>
        api.post<ApiResponse<Department | { department: Department; created: number; skipped: number; hospitalCount: number }>>(
            '/api/hospital-admin/add/department',
            data
        ).then(res => res.data.data),

    // OPD Fees
    getOpdFees: () => api.get<ApiResponse<OpdFee[]>>('/api/hospital-admin/opd-charge/fetch').then(res => res.data.data),
    getDoctors: () => api.get<ApiResponse<Doctor[]>>('/api/hospital-admin/staff/fetch').then(res => res.data.data),
    createOpdFee: (data: { doctorId: string; amount: number }) =>
        api.post<ApiResponse<OpdFee>>(`/api/hospital-admin/opd-charge/create?doctorId=${data.doctorId}`, data).then(res => res.data.data),
    updateOpdFee: (id: string, data: { amount: number }) =>
        api.patch<ApiResponse<OpdFee>>(`/api/hospital-admin/opd-charge/update/${id}`, data).then(res => res.data.data),
    deleteOpdFee: (id: string) =>
        api.delete<ApiResponse<void>>(`/api/hospital-admin/opd-charge/delete/${id}`).then(res => res.data.data),

    // Staff Management
    getStaffById: (id: string) => api.get<ApiResponse<HospitalStaff>>(`/api/hospital-admin/staff/fetch/${id}`).then(res => res.data.data),
    getStaff: (opts?: { includeInactive?: boolean; hospitalId?: string; allHospitals?: boolean }) =>
        api.get<ApiResponse<Staff[]>>('/api/hospital-admin/staff/fetch', {
            params: {
                ...(opts?.includeInactive ? { includeInactive: true } : {}),
                ...(opts?.hospitalId ? { hospitalId: opts.hospitalId } : {}),
                ...(opts?.allHospitals ? { allHospitals: true } : {}),
            },
        }).then(res => res.data.data),
    createStaff: (data: StaffFormData, files?: StaffOnboardingFiles) => {
        const fd = new FormData();
        appendStaffFormFields(fd, data);
        appendStaffOnboardingFiles(fd, files);
        return api
            .post<ApiResponse<Staff>>('/api/hospital-admin/staff/add', fd)
            .then((res) => res.data.data);
    },
    updateStaff: (id: string, data: Partial<StaffFormData>, files?: StaffOnboardingFiles) => {
        const fd = new FormData();
        appendStaffFormFields(fd, data);
        appendStaffOnboardingFiles(fd, files);
        return api
            .patch<ApiResponse<Staff>>(`/api/hospital-admin/staff/update/${id}`, fd)
            .then((res) => res.data.data);
    },
    deleteStaff: (id: string) =>
        api.delete<ApiResponse<void>>(`/api/hospital-admin/staff/delete/${id}`).then(res => res.data.data),

    // Shifts
    getShifts: () => api.get<ApiResponse<Shift[]>>('/api/hospital-admin/shifts').then(res => res.data.data),
    createShift: (data: any) =>
        api.post<ApiResponse<Shift>>('/api/hospital-admin/shift/create', data).then(res => res.data.data),
    updateShift: (id: string, data: Partial<Omit<Shift, 'id' | 'status'>>) =>
        api.patch<ApiResponse<Shift>>(`/api/hospital-admin/shift/update/${id}`, data).then(res => res.data.data),
    deleteShift: (id: string) =>
        api.delete<ApiResponse<void>>(`/api/hospital-admin/shift/delete/${id}`).then(res => res.data.data),

    // Temp Shifts
    getTempShifts: (staffId: string) => api.get<ApiResponse<Shift[]>>(`/api/hospital-admin/shift/temp-shift/${staffId}`).then(res => res.data.data),
    createTempShift: (data: TempShift) =>
        api.post<ApiResponse<TempShift>>('/api/hospital-admin/shift/create-temp', data).then(res => res.data.data),
    deleteTempShift: (id: string) =>
        api.delete<ApiResponse<void>>(`/api/hospital-admin/shift/temp-shift/${id}`).then(res => res.data.data),

    // Get hospital KPIs by date range
    getKpisByDate: (startDate: string, endDate: string, hospitalId?: string) =>
        api.get<ApiResponse<HospitalAdminKpis>>('/api/hospital-admin/kpis-by-interval', {
            params:
                hospitalId != null && hospitalId !== ''
                    ? { startDate, endDate, hospitalId }
                    : { startDate, endDate },
        }).then(res => res.data.data),

    // Mini CRM: round robin lead distribution
    getLeadRoundRobinConfig: () =>
        api.get<ApiResponse<RoundRobinConfig>>('/api/hospital-admin/crm/round-robin')
            .then(res => res.data.data),
    setLeadRoundRobinConfig: (enabled: boolean) =>
        api.patch<ApiResponse<RoundRobinConfig>>('/api/hospital-admin/crm/round-robin', { enabled })
            .then(res => res.data.data),

    // Mini CRM: agency users + API keys
    listAgencyUsers: () =>
        api.get<ApiResponse<AgencyUserOption[]>>('/api/hospital-admin/crm/agency-users')
            .then(res => res.data.data),
    listAgencyApiKeys: () =>
        api.get<ApiResponse<AgencyApiKeyRow[]>>('/api/hospital-admin/crm/agency-keys', { timeout: 180_000 })
            .then(res => res.data.data),
    createAgencyApiKey: (payload: { name: string; assignedToId: string }) =>
        api.post<ApiResponse<AgencyApiKeyRow>>('/api/hospital-admin/crm/agency-keys', payload, { timeout: 180_000 })
            .then(res => res.data.data),
    updateAgencyApiKey: (id: string, payload: { name?: string; assignedToId?: string }) =>
        api.patch<ApiResponse<AgencyApiKeyRow>>(`/api/hospital-admin/crm/agency-keys/${id}`, payload, { timeout: 180_000 })
            .then(res => res.data.data),
    setAgencyApiKeyStatus: (id: string, isActive: boolean) =>
        api.patch<ApiResponse<AgencyApiKeyRow>>(`/api/hospital-admin/crm/agency-keys/${id}/status`, { isActive }, { timeout: 180_000 })
            .then(res => res.data.data),
    rotateAgencyApiKey: (id: string) =>
        api.post<ApiResponse<AgencyApiKeyRow>>(`/api/hospital-admin/crm/agency-keys/${id}/rotate`, undefined, { timeout: 180_000 })
            .then(res => res.data.data),

    // Mini CRM: round robin assignees (in-house sales persons)
    listRoundRobinAssignees: () =>
        api.get<ApiResponse<RoundRobinAssigneePayload>>('/api/hospital-admin/crm/round-robin/assignees')
            .then(res => res.data.data),
    setRoundRobinAssigneeActive: (staffId: string, isActive: boolean) =>
        api.patch<ApiResponse<{ id: string; isLeadRoundRobinActive: boolean }>>(
            `/api/hospital-admin/crm/round-robin/assignees/${staffId}`,
            { isActive }
        ).then(res => res.data.data),
    setRoundRobinAssigneeDeniedSources: (staffId: string, deniedSources: string[]) =>
        api.patch<ApiResponse<{ id: string; leadDeniedSources: string[] }>>(
            `/api/hospital-admin/crm/round-robin/assignees/${staffId}/denied-sources`,
            { deniedSources }
        ).then(res => res.data.data),
    setRoundRobinAssigneeManager: (staffId: string, managerId: string | null) =>
        api.patch<ApiResponse<{ id: string; reportsToManagerId: string | null }>>(
            `/api/hospital-admin/crm/round-robin/assignees/${staffId}/manager`,
            { managerId }
        ).then(res => res.data.data),
    markRoundRobinAssigneeExited: (staffId: string, managerId: string | null) =>
        api.patch<ApiResponse<{ reassignedLeads: number }>>(
            `/api/hospital-admin/crm/round-robin/assignees/${staffId}/exit`,
            { managerId }
        ).then(res => res.data.data),

    // Mini CRM: WhatsApp inbound keyword auto-reply rules
    listWhatsAppAutoReplyRules: () =>
        api.get<ApiResponse<WhatsAppAutoReplyRule[]>>('/api/hospital-admin/crm/whatsapp-auto-reply-rules', { timeout: 180_000 })
            .then(res => res.data.data),
    createWhatsAppAutoReplyRule: (payload: {
        name: string;
        keywords: string[];
        replyText: string;
        priority?: number;
        isActive?: boolean;
    }) =>
        api.post<ApiResponse<WhatsAppAutoReplyRule>>('/api/hospital-admin/crm/whatsapp-auto-reply-rules', payload, { timeout: 180_000 })
            .then(res => res.data.data),
    updateWhatsAppAutoReplyRule: (id: string, payload: Partial<{
        name: string;
        keywords: string[];
        replyText: string;
        priority: number;
        isActive: boolean;
    }>) =>
        api.patch<ApiResponse<WhatsAppAutoReplyRule>>(`/api/hospital-admin/crm/whatsapp-auto-reply-rules/${id}`, payload, { timeout: 180_000 })
            .then(res => res.data.data),
    deleteWhatsAppAutoReplyRule: (id: string) =>
        api.delete<ApiResponse<{ id: string }>>(`/api/hospital-admin/crm/whatsapp-auto-reply-rules/${id}`, { timeout: 180_000 })
            .then(res => res.data.data),
    seedDefaultWhatsAppAutoReplyRules: () =>
        api.post<ApiResponse<{ created: number; skipped: number; totalDefaults: number }>>(
            '/api/hospital-admin/crm/whatsapp-auto-reply-rules/seed-defaults'
        , undefined, { timeout: 180_000 }).then(res => res.data.data),

    // Mini CRM: WhatsApp Cloud API numbers (multi-number support)
    listHospitalWhatsAppNumbers: () =>
        api.get<ApiResponse<HospitalWhatsAppNumber[]>>('/api/hospital-admin/crm/whatsapp-numbers', { timeout: 180_000 })
            .then(res => res.data.data),
    createHospitalWhatsAppNumber: (payload: {
        phoneNumberId: string;
        accessToken: string;
        wabaId?: string | null;
        name?: string | null;
        leadIngestApiKeyId?: string | null;
        isActive?: boolean;
        isDefault?: boolean;
    }) =>
        api.post<ApiResponse<HospitalWhatsAppNumber>>('/api/hospital-admin/crm/whatsapp-numbers', payload, { timeout: 180_000 })
            .then(res => res.data.data),
    updateHospitalWhatsAppNumber: (id: string, payload: Partial<{
        phoneNumberId: string;
        accessToken: string;
        wabaId: string | null;
        name: string | null;
        leadIngestApiKeyId: string | null;
        isActive: boolean;
        isDefault: boolean;
    }>) =>
        api.patch<ApiResponse<HospitalWhatsAppNumber>>(`/api/hospital-admin/crm/whatsapp-numbers/${id}`, payload, { timeout: 180_000 })
            .then(res => res.data.data),
    deleteHospitalWhatsAppNumber: (id: string) =>
        api.delete<ApiResponse<{ id: string }>>(`/api/hospital-admin/crm/whatsapp-numbers/${id}`, { timeout: 180_000 })
            .then(res => res.data.data),

    // Marketing > Meta Integration (super admin configures per hospital)
    getMetaIntegrationConfig: (hospitalId?: string) =>
        api.get<ApiResponse<MetaIntegrationConfig>>('/api/hospital-admin/marketing/meta-settings', {
            params: hospitalId ? { hospitalId } : undefined,
        }).then(res => res.data.data),
    saveMetaIntegrationConfig: (
        payload: {
            enabled?: boolean;
            datasetId?: string;
            accessToken?: string;
            testMode?: boolean;
            testEventCode?: string;
            sendRevenueEvents?: boolean;
            revenueValueMode?: MetaRevenueValueMode;
            eventMapping?: Partial<Record<MetaThmsAction, string>>;
        },
        hospitalId?: string
    ) =>
        api.put<ApiResponse<MetaIntegrationConfig>>('/api/hospital-admin/marketing/meta-settings', payload, {
            params: hospitalId ? { hospitalId } : undefined,
        }).then(res => res.data.data),
    sendMetaTestEvent: (hospitalId?: string) =>
        api.post<ApiResponse<{ ok: boolean; status: number; body: unknown; queueId: string }>>(
            '/api/hospital-admin/marketing/meta-test-event',
            {},
            { params: hospitalId ? { hospitalId } : undefined }
        ).then(res => res.data.data),
    getMetaDashboard: () =>
        api.get<ApiResponse<MetaDashboardStats>>('/api/hospital-admin/marketing/meta-dashboard')
            .then(res => res.data.data),
    listMetaConversionLogs: (params?: {
        page?: number;
        limit?: number;
        status?: MetaEventQueueStatus;
    }) =>
        api.get<ApiResponse<{ total: number; page: number; limit: number; rows: MetaEventLogRow[] }>>(
            '/api/hospital-admin/marketing/meta-event-logs',
            { params }
        ).then(res => res.data.data),
    retryMetaConversionEvent: (id: string) =>
        api.post<ApiResponse<MetaEventLogRow>>(`/api/hospital-admin/marketing/meta-event-logs/${id}/retry`)
            .then(res => res.data.data),

    // Marketing > Google Ads Integration (super admin configures per hospital)
    getGoogleIntegrationConfig: (hospitalId?: string) =>
        api.get<ApiResponse<GoogleAdsIntegrationConfig>>('/api/hospital-admin/marketing/google-settings', {
            params: hospitalId ? { hospitalId } : undefined,
        }).then(res => res.data.data),
    saveGoogleIntegrationConfig: (payload: Partial<GoogleAdsIntegrationConfig> & { hospitalId: string }) =>
        api.put<ApiResponse<GoogleAdsIntegrationConfig>>('/api/hospital-admin/marketing/google-settings', payload)
            .then(res => res.data.data),
    sendGoogleTestConversion: (
        params?: { hospitalId?: string; eventName?: string; leadId?: string }
    ) =>
        api.post<
            ApiResponse<{
                ok: boolean;
                queueId: string;
                eventName: string;
                validateOnly?: boolean;
                leadId?: string;
                error?: string | null;
                response?: unknown;
            }>
        >(
            '/api/hospital-admin/marketing/google-test-conversion',
            {
                ...(params?.eventName ? { eventName: params.eventName } : {}),
                ...(params?.leadId ? { leadId: params.leadId } : {}),
            },
            { params: params?.hospitalId ? { hospitalId: params.hospitalId } : undefined }
        ).then(res => res.data.data),
    listGoogleConversionLogs: (params?: {
        page?: number;
        limit?: number;
        status?: GoogleEventQueueStatus;
        eventName?: string;
        hospitalId?: string;
    }) =>
        api.get<ApiResponse<{ total: number; page: number; limit: number; rows: GoogleEventQueueRow[] }>>(
            '/api/hospital-admin/marketing/google-event-logs',
            { params }
        ).then(res => res.data.data),
    retryGoogleConversionEvent: (id: string) =>
        api.post<ApiResponse<GoogleEventQueueRow>>(`/api/hospital-admin/marketing/google-event-logs/${id}/retry`)
            .then(res => res.data.data),
    processPendingGoogleConversionEvents: () =>
        api.post<ApiResponse<{ processed: number; remaining: number }>>(
            '/api/hospital-admin/marketing/google-event-logs/process-pending'
        ).then(res => res.data.data),
    getCampaignAttribution: (params: { from: string; to: string; campaignId?: string }) =>
        api.get<ApiResponse<{ rows: CampaignAttributionRow[]; totals: Omit<CampaignAttributionRow, 'campaignId' | 'campaignName'> }>>(
            '/api/hospital-admin/marketing/meta-attribution',
            { params }
        ).then(res => res.data.data),

    getSarvamVoiceAiSettings: () =>
        api.get<ApiResponse<SarvamVoiceAiSettings>>('/api/hospital-admin/crm/sarvam-settings')
            .then(res => res.data.data),
    saveSarvamVoiceAiSettings: (payload: {
        enabled?: boolean;
        agentEnabled?: boolean;
        apiKey?: string;
        sarvamOrgId?: string;
        sarvamWorkspaceId?: string;
        defaultAgentId?: string;
        callingWindow?: {
            startTime?: string;
            endTime?: string;
            startMinutes?: number;
            endMinutesExclusive?: number;
        };
        leadSourcesAllowlist?: string[];
        campaignNamesAllowlist?: string[];
        assignedToStaffIdsAllowlist?: string[];
        sarvamOpdAssignStaffIdsAllowlist?: string[];
        callUnassignedLeads?: boolean;
        callDelayMinutes?: number;
        rolloutMode?: 'TEST' | 'LIVE';
        appointmentReminderEnabled?: boolean;
        appointmentReminderAgentAppId?: string;
        appointmentReminderRolloutMode?: 'TEST' | 'LIVE';
        appointmentReminderLeadSourcesAllowlist?: string[];
        dnpNotReachableRetryEnabled?: boolean;
        dnpNotReachableAgentAppId?: string;
        dnpNotReachableRolloutMode?: 'TEST' | 'LIVE';
        dnpPastLeadsAgeBuckets?: SarvamDnpPastAgeBuckets;
        agent?: Partial<SarvamAgentConfigRow>;
    }) =>
        api.put<ApiResponse<null>>('/api/hospital-admin/crm/sarvam-settings', payload)
            .then(res => res.data.data),
    listSarvamCampaignOptions: () =>
        api.get<ApiResponse<string[]>>('/api/hospital-admin/crm/sarvam-campaign-options')
            .then(res => res.data.data),
    listSarvamDispositionMappings: (sarvamAppId?: string) =>
        api.get<ApiResponse<SarvamDispositionMappingRow[]>>('/api/hospital-admin/crm/sarvam-disposition-mappings', {
            params: sarvamAppId ? { sarvamAppId } : undefined,
        }).then(res => res.data.data),
    saveSarvamDispositionMappings: (payload: {
        sarvamAppId: string;
        mappings: SarvamDispositionMappingRow[];
    }) =>
        api.put<ApiResponse<null>>('/api/hospital-admin/crm/sarvam-disposition-mappings', payload)
            .then(res => res.data.data),
    seedSarvamDispositionMappings: (sarvamAppId?: string) =>
        api
            .post<ApiResponse<{ created: number; skipped: number }>>(
                '/api/hospital-admin/crm/sarvam-seed-disposition-mappings',
                sarvamAppId ? { sarvamAppId } : {}
            )
            .then((res) => res.data.data),
    listSarvamDoctorMappings: (sarvamAppId?: string) =>
        api
            .get<ApiResponse<SarvamDoctorMappingRow[]>>('/api/hospital-admin/crm/sarvam-doctor-mappings', {
                params: sarvamAppId ? { sarvamAppId } : undefined,
            })
            .then((res) => res.data.data),
    saveSarvamDoctorMappings: (payload: { sarvamAppId: string; mappings: SarvamDoctorMappingRow[] }) =>
        api
            .put<ApiResponse<null>>('/api/hospital-admin/crm/sarvam-doctor-mappings', payload)
            .then((res) => res.data.data),
    seedSarvamDoctorMappings: (sarvamAppId?: string) =>
        api
            .post<ApiResponse<{ created: number; skipped: number }>>(
                '/api/hospital-admin/crm/sarvam-seed-doctor-mappings',
                sarvamAppId ? { sarvamAppId } : {}
            )
            .then((res) => res.data.data),
    listSarvamCampaignProcedureMappings: () =>
        api
            .get<ApiResponse<SarvamCampaignProcedureMappingRow[]>>(
                '/api/hospital-admin/crm/sarvam-campaign-procedure-mappings'
            )
            .then((res) => res.data.data ?? []),
    saveSarvamCampaignProcedureMappings: (payload: {
        mappings: SarvamCampaignProcedureMappingRow[];
    }) =>
        api
            .put<ApiResponse<null>>(
                '/api/hospital-admin/crm/sarvam-campaign-procedure-mappings',
                payload
            )
            .then((res) => res.data),
    getSarvamBillingSummary: (params?: { from?: string; to?: string; hospitalId?: string }) =>
        api
            .get<ApiResponse<SarvamBillingSummary>>('/api/hospital-admin/crm/sarvam-billing', { params })
            .then((res) => res.data.data),
    getSarvamPricingRates: () =>
        api
            .get<ApiResponse<SarvamPricingRatesResponse>>('/api/hospital-admin/crm/sarvam-pricing-rates')
            .then((res) => res.data.data),
    saveSarvamPricingRates: (payload: { voiceRatePerMinuteInr: number }) =>
        api
            .put<ApiResponse<{ voiceRatePerMinuteInr: number }>>(
                '/api/hospital-admin/crm/sarvam-pricing-rates',
                payload
            )
            .then((res) => res.data.data),
    listSarvamLeadCalls: (params?: {
        from?: string;
        to?: string;
        hospitalId?: string;
        sarvamAppId?: string;
        leadSource?: string;
        assignedToId?: string;
        limit?: number;
    }) =>
        api
            .get<ApiResponse<SarvamLeadCallLogListResponse>>('/api/hospital-admin/crm/sarvam-lead-calls', {
                params,
            })
            .then((res) => res.data.data),
    downloadSarvamLeadCallsCsv: (params?: {
        from?: string;
        to?: string;
        hospitalId?: string;
        sarvamAppId?: string;
        leadSource?: string;
        assignedToId?: string;
        limit?: number;
    }) =>
        api.get('/api/hospital-admin/crm/sarvam-lead-calls', {
            params: { ...params, format: 'csv', limit: params?.limit ?? 5000 },
            responseType: 'blob',
        }),
    retrySarvamLeadCall: (id: string) =>
        api
            .post<ApiResponse<null>>(`/api/hospital-admin/crm/sarvam-lead-calls/${id}/retry`)
            .then((res) => res.data),
};
